import {
  Alert, Anchor, Badge, Box, Button, Card, FileButton, Group, List, MultiSelect, NavLink, NumberInput, Paper, PasswordInput, Progress, Select,
  SimpleGrid, Skeleton, Stack, Switch, Table, Text, Textarea, TextInput, ThemeIcon, Title,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import {
  IconCircleCheck, IconCircleX, IconFiles, IconScale, IconInfoCircle, IconMail, IconPlug, IconSatellite, IconSend, IconServer, IconSettings,
  IconShieldLock, IconLockAccess, IconUserCheck, IconWifi, IconFileCertificate, IconPhotoUp, IconDatabase,
} from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { http } from "../../api/http";
import { DataTable } from "../../components/DataTable";
import { fmtDateTime, fmtNum, PageHeader, plural } from "../../components/ui";
import { useT } from "../../i18n/script";
import AdminLayout from "./AdminLayout";

type ItemType = "bool" | "int" | "float" | "text" | "url" | "email" | "textarea" | "select" | "multiselect" | "secret" | "image";
export interface SettingItem {
  key: string; label: string; type: ItemType; default: unknown; help?: string | null; options?: { value: string; label: string }[] | null;
  min?: number | null; max?: number | null; unit?: string | null; placeholder?: string | null; depends_on?: string | null;
  value: unknown; is_set?: boolean; updated_by?: string | null; updated_at?: string | null;
}
export interface SettingSection { code: string; title: string; description?: string; icon?: string; items: SettingItem[] }
interface SentinelStatus { configured: boolean; enabled: boolean; base_url?: string; month_requests: number; month_cache_hits?: number;
  month_limit: number; month_pu?: number; pu_limit?: number; defaults?: Record<string, unknown> }

const ICONS: Record<string, ReactNode> = {
  settings: <IconSettings size={17} />, "user-check": <IconUserCheck size={17} />, mail: <IconMail size={17} />,
  satellite: <IconSatellite size={17} />, "shield-lock": <IconShieldLock size={17} />, vpn: <IconLockAccess size={17} />, files: <IconFiles size={17} />, plug: <IconPlug size={17} />, scale: <IconScale size={17} />, "file-certificate": <IconFileCertificate size={17} />,
  database: <IconDatabase size={17} />,
};
const INFO_SECTION = "verzije";
// depends_on: "kljuc" (bool uključen) ili "kljuc=vrijednost"; zavisnost se lančano provjerava (npr. dan → učestalost → uključeno)
function shown(i: SettingItem, form: Record<string, unknown>, all?: SettingItem[]): boolean {
  if (!i.depends_on) return true;
  const [k, v] = i.depends_on.split("=");
  const ok = v === undefined ? form[k] === true : String(form[k] ?? "") === v;
  const parent = all?.find((x) => x.key === k);
  return ok && (!parent || shown(parent, form, all));
}
const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

export default function SettingsPage() {
  const { t } = useT();
  const qc = useQueryClient();
  const [sp, setSp] = useSearchParams();
  const wide = useMediaQuery("(min-width: 75em)", true);
  const q = useQuery({ queryKey: ["settings"], queryFn: () => http.get<SettingSection[]>("/api/v1/settings") });
  const sections = q.data ?? [];

  const [form, setForm] = useState<Record<string, unknown>>({});
  const [secrets, setSecrets] = useState<Record<string, string>>({}); // ključ prisutan = mijenja se ("" = brisanje)
  const orig = useMemo(() => Object.fromEntries(sections.flatMap((s) => s.items.filter((i) => i.type !== "secret").map((i) => [i.key, i.value]))), [sections]);
  useEffect(() => { setForm(orig); setSecrets({}); }, [orig]);

  const active = sp.get("sekcija") ?? sections[0]?.code ?? null;
  const setActive = (c: string) => setSp((p) => { const n = new URLSearchParams(p); n.set("sekcija", c); return n; }, { replace: true });

  const changedKeys = [...Object.keys(form).filter((k) => !same(form[k], orig[k])), ...Object.keys(secrets)];
  const dirtyBySection = (code: string) => sections.find((s) => s.code === code)?.items.filter((i) => changedKeys.includes(i.key)).length ?? 0;

  const save = useMutation({
    mutationFn: () => {
      const values: Record<string, unknown> = {};
      Object.keys(form).forEach((k) => { if (!same(form[k], orig[k])) values[k] = form[k]; });
      Object.entries(secrets).forEach(([k, v]) => { values[k] = v; });
      return http.put<{ changed: string[]; keycloak: string[] }>("/api/v1/settings", { values });
    },
    onSuccess: (r) => {
      notifications.show({ color: "green", title: t("Sačuvano"),
        message: `${t("Izmijenjeno podešavanja")}: ${r.changed.length}` + (r.keycloak?.length ? ` · Keycloak: ${r.keycloak.join(", ")}` : "") });
      qc.invalidateQueries({ queryKey: ["settings"] });
      qc.invalidateQueries({ queryKey: ["sentinel-status"] });
    },
    onError: (e: Error) => notifications.show({ color: "red", title: t("Nije sačuvano"), message: e.message }),
  });
  const reset = () => { setForm(orig); setSecrets({}); };

  const section = sections.find((s) => s.code === active);
  const navItems = [...sections.map((s) => ({ code: s.code, title: s.title, icon: ICONS[s.icon ?? ""] ?? <IconSettings size={17} /> })),
    { code: INFO_SECTION, title: "Verzije komponenti", icon: <IconServer size={17} /> }];

  return (
    <AdminLayout>
      <PageHeader title="Podešavanja sistema" description="Sva podešavanja su grupisana po oblastima. Izmjene se čuvaju klikom na „Sačuvaj“ i bilježe u istoriji aktivnosti." />
      {q.isLoading ? <Skeleton h={400} radius="lg" /> : q.isError ? <Alert color="red">{(q.error as Error).message}</Alert> : (
        <Box style={{ display: "flex", gap: 20, alignItems: "flex-start", flexDirection: wide ? "row" : "column" }}>
          {wide ? (
            <Paper withBorder radius="lg" p={6} w={250} style={{ position: "sticky", top: 72, flexShrink: 0 }}>
              {navItems.map((n) => {
                const d = dirtyBySection(n.code);
                return (
                  <NavLink key={n.code} label={t(n.title)} leftSection={n.icon} active={active === n.code} variant="light"
                    onClick={() => setActive(n.code)} styles={{ root: { borderRadius: 8 } }}
                    rightSection={d ? <Badge size="xs" color="orange" circle>{d}</Badge> : undefined} />
                );
              })}
            </Paper>
          ) : (
            <Select w="100%" value={active} allowDeselect={false} onChange={(v) => v && setActive(v)}
              data={navItems.map((n) => ({ value: n.code, label: t(n.title) + (dirtyBySection(n.code) ? " •" : "") }))} />
          )}
          <Box style={{ flex: 1, minWidth: 0, width: "100%" }}>
            {active === INFO_SECTION ? <VersionsPanel /> : section ? (
              <Paper withBorder radius="lg" p={{ base: "md", sm: "lg" }}>
                <Group gap="sm" mb={4} wrap="nowrap">
                  <ThemeIcon variant="light" size={36} radius="md">{ICONS[section.icon ?? ""] ?? <IconSettings size={17} />}</ThemeIcon>
                  <Title order={4}>{t(section.title)}</Title>
                </Group>
                {section.description && <Text size="sm" c="dimmed" mb="md">{t(section.description)}</Text>}
                {section.code === "privatnost" && (
                  <Alert icon={<IconShieldLock />} color="blue" variant="light" mb="md">
                    {t("Izmjene pravila zaštite ličnih podataka primjenjuju se odmah na sve korisnike (prikaz imena, izvoz, prag k-anonimnosti) i trajno se bilježe u istoriji aktivnosti sa vašim imenom.")}
                  </Alert>
                )}
                {section.code === "satelit" && <SatelliteGuide />}
                {section.code === "mreza" && <NetworkStatus />}
                <Stack gap="lg">
                  {section.items.filter((i) => shown(i, form, section.items)).map((i) => (
                    <Field key={i.key} item={i} value={form[i.key]} changed={changedKeys.includes(i.key)}
                      onChange={(v) => setForm((f) => ({ ...f, [i.key]: v }))}
                      secret={secrets[i.key]} onSecret={(v) => setSecrets((s) => {
                        const n = { ...s };
                        if (v === undefined) delete n[i.key]; else n[i.key] = v;
                        return n;
                      })} />
                  ))}
                </Stack>
                {section.code === "smtp" && <SmtpTest dirty={dirtyBySection("smtp") > 0} />}
                {section.code === "satelit" && <SatelliteTools dirty={dirtyBySection("satelit") > 0} />}
                {section.code === "backup" && form["backup.odrediste"] === "sftp" && <BackupTest dirty={dirtyBySection("backup") > 0} />}
              </Paper>
            ) : null}

            {changedKeys.length > 0 && (
              <Paper withBorder radius="lg" shadow="md" p="sm" mt="md"
                style={{ position: "sticky", bottom: 12, zIndex: 5, borderColor: "var(--mantine-color-orange-4)" }}>
                <Group justify="space-between" wrap="wrap" gap="xs">
                  <Group gap={8}>
                    <Badge color="orange" variant="filled" circle>{changedKeys.length}</Badge>
                    <Text size="sm" fw={600}>{t(plural(changedKeys.length, "izmjena nije sačuvana", "izmjene nisu sačuvane", "izmjena nije sačuvano"))}</Text>
                    <Text size="xs" c="dimmed" visibleFrom="sm">
                      {sections.filter((s) => dirtyBySection(s.code)).map((s) => t(s.title)).join(", ")}
                    </Text>
                  </Group>
                  <Group gap="xs">
                    <Button variant="default" onClick={reset} disabled={save.isPending}>{t("Poništi")}</Button>
                    <Button onClick={() => save.mutate()} loading={save.isPending}>{t("Sačuvaj")}</Button>
                  </Group>
                </Group>
              </Paper>
            )}
          </Box>
        </Box>
      )}
    </AdminLayout>
  );
}

function Field({ item: i, value, onChange, changed, secret, onSecret }: {
  item: SettingItem; value: unknown; onChange: (v: unknown) => void; changed: boolean;
  secret?: string; onSecret: (v: string | undefined) => void;
}) {
  const { t } = useT();
  const label = (
    <Group gap={6} component="span" wrap="nowrap">
      <span>{t(i.label)}</span>
      {changed && <Badge size="xs" color="orange" variant="light">{t("izmijenjeno")}</Badge>}
    </Group>
  );
  const help = i.help ? t(i.help) : undefined;
  const meta = i.updated_by || i.updated_at ? (
    <Text size="xs" c="dimmed" mt={4}>{t("izmijenio")} {i.updated_by ?? "—"}, {fmtDateTime(i.updated_at)}</Text>
  ) : null;
  const opts = (i.options ?? []).map((o) => ({ value: String(o.value), label: t(o.label) }));
  let ctl: ReactNode;
  switch (i.type) {
    case "bool":
      ctl = (
        <Card withBorder radius="md" p="sm" bg={value ? "var(--mantine-color-agri-0)" : undefined}>
          <Switch checked={!!value} onChange={(e) => onChange(e.currentTarget.checked)} label={label} description={help} size="md" />
        </Card>
      );
      return <div>{ctl}{meta}</div>;
    case "int":
    case "float":
      ctl = <NumberInput label={label} description={help} value={value as number ?? ""} min={i.min ?? undefined} max={i.max ?? undefined}
        allowDecimal={i.type === "float"} placeholder={i.placeholder ?? undefined} maw={320}
        rightSection={i.unit ? <Text size="xs" c="dimmed" pr={8}>{t(i.unit)}</Text> : undefined} rightSectionWidth={i.unit ? 60 : undefined}
        onChange={(v) => onChange(v === "" ? null : Number(v))} />;
      break;
    case "textarea":
      ctl = <Textarea label={label} description={help} autosize minRows={2} maxRows={12} value={String(value ?? "")}
        placeholder={i.placeholder ?? undefined} onChange={(e) => onChange(e.currentTarget.value)} />;
      break;
    case "select":
      ctl = <Select label={label} description={help} data={opts} value={value == null ? null : String(value)} maw={520}
        allowDeselect={false} onChange={(v) => onChange(v)} />;
      break;
    case "multiselect":
      ctl = <MultiSelect label={label} description={help} data={opts} value={Array.isArray(value) ? value.map(String) : []}
        searchable clearable onChange={(v) => onChange(v)} />;
      break;
    case "secret": {
      const willClear = secret === "";
      ctl = (
        <div>
          <PasswordInput label={label} description={help} maw={520} value={secret ?? ""} disabled={willClear}
            placeholder={i.is_set ? t("•••••• (ostavite prazno za bez promjene)") : t("nije postavljeno — unesite vrijednost")}
            onChange={(e) => onSecret(e.currentTarget.value === "" ? undefined : e.currentTarget.value)}
            autoComplete="new-password" />
          <Group gap="xs" mt={6}>
            {willClear ? <Badge color="red" variant="light">{t("biće uklonjeno")}</Badge>
              : secret ? <Badge color="orange" variant="light">{t("nova vrijednost")}</Badge>
              : i.is_set ? <Badge color="green" variant="light" leftSection={<IconCircleCheck size={12} />}>{t("postavljeno")}</Badge>
              : <Badge color="gray" variant="light">{t("nije postavljeno")}</Badge>}
            {i.is_set && !willClear && <Anchor size="xs" c="red" onClick={() => onSecret("")}>{t("Ukloni")}</Anchor>}
            {willClear && <Anchor size="xs" onClick={() => onSecret(undefined)}>{t("Odustani")}</Anchor>}
          </Group>
        </div>
      );
      break;
    }
    case "image": {
      const src = String(value ?? "");
      const pick = (f: File | null) => {
        if (!f) return;
        if (f.size > 400_000) { notifications.show({ color: "red", message: t("Logo je veći od 400 kB") }); return; }
        const r = new FileReader();
        r.onload = () => onChange(String(r.result));
        r.readAsDataURL(f);
      };
      ctl = (
        <div>
          <Text size="sm" fw={500}>{label}</Text>
          {help && <Text size="xs" c="dimmed" mb={6}>{help}</Text>}
          <Group align="center" gap="md">
            <Paper withBorder radius="md" p={8} w={120} h={90} style={{ display: "grid", placeItems: "center", background: "#fff" }}>
              {src ? <img src={src} alt={t("Logo")} style={{ maxWidth: 100, maxHeight: 72 }} /> : <Text size="xs" c="dimmed">{t("nije postavljen")}</Text>}
            </Paper>
            <Stack gap={6}>
              <FileButton onChange={pick} accept="image/png,image/jpeg,image/svg+xml">
                {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconPhotoUp size={14} />}>{t(src ? "Zamijeni logo" : "Postavi logo")}</Button>}
              </FileButton>
              {src && <Anchor size="xs" c="red" onClick={() => onChange("")}>{t("Ukloni")}</Anchor>}
            </Stack>
          </Group>
        </div>
      );
      break;
    }
    default:
      ctl = <TextInput label={label} description={help} value={String(value ?? "")} maw={i.type === "url" ? undefined : 520}
        type={i.type === "email" ? "email" : i.type === "url" ? "url" : "text"} placeholder={i.placeholder ?? undefined}
        onChange={(e) => onChange(e.currentTarget.value)} />;
  }
  return <div>{ctl}{meta}</div>;
}

function SmtpTest({ dirty }: { dirty: boolean }) {
  const { t } = useT();
  const [to, setTo] = useState("");
  const m = useMutation({
    mutationFn: () => http.post<{ ok: boolean; to: string }>("/api/v1/settings/test/smtp", to ? { to } : {}),
    onSuccess: (r) => notifications.show({ color: "green", title: t("Poruka poslata"), message: `${t("Provjerite sandučić")}: ${r.to}` }),
    onError: (e: Error) => notifications.show({ color: "red", title: t("Slanje nije uspjelo"), message: e.message }),
  });
  return (
    <Card withBorder radius="md" mt="xl" bg="var(--mantine-color-default-hover)">
      <Text fw={650} mb={4}>{t("Pošalji probnu poruku")}</Text>
      <Text size="xs" c="dimmed" mb="sm">{t("Prazno polje = poruka ide na vašu e-poštu iz naloga.")}{dirty ? " " + t("Proba koristi sačuvana podešavanja — prvo sačuvajte izmjene.") : ""}</Text>
      <Group align="flex-end" gap="xs">
        <TextInput type="email" placeholder="ime@primjer.org" value={to} onChange={(e) => setTo(e.currentTarget.value)} style={{ flex: 1, maxWidth: 360 }} />
        <Button leftSection={<IconSend size={16} />} variant="light" loading={m.isPending} onClick={() => m.mutate()}>{t("Pošalji")}</Button>
      </Group>
    </Card>
  );
}

function BackupTest({ dirty }: { dirty: boolean }) {
  const { t } = useT();
  const qc = useQueryClient();
  const [res, setRes] = useState<{ ok: boolean; message: string } | null>(null);
  const m = useMutation({
    mutationFn: () => http.post<{ ok: boolean; message: string; saved_fingerprint?: boolean }>("/api/v1/settings/test/backup"),
    onSuccess: (r) => { setRes(r); if (r.saved_fingerprint) qc.invalidateQueries({ queryKey: ["settings"] }); },
    onError: (e: Error) => setRes({ ok: false, message: e.message }),
  });
  return (
    <Card withBorder radius="md" mt="xl" bg="var(--mantine-color-default-hover)">
      <Text fw={650} mb={4}>{t("Provjera SFTP odredišta")}</Text>
      <Text size="xs" c="dimmed" mb="sm">{t("Spaja se na server, pravi folder ako ne postoji i probno upisuje fajl. Kopija se šalje poslije svakog backup-a — za probu u Administracija → Backup kliknite „Pokreni backup sada“.")}{dirty ? " " + t("Proba koristi sačuvana podešavanja — prvo sačuvajte izmjene.") : ""}</Text>
      <Button leftSection={<IconWifi size={16} />} variant="light" loading={m.isPending} onClick={() => m.mutate()}>{t("Provjeri vezu")}</Button>
      {res && (
        <Alert mt="sm" p="xs" color={res.ok ? "green" : "red"} icon={res.ok ? <IconCircleCheck size={16} /> : <IconCircleX size={16} />}>
          <Text size="xs" data-no-translit="true">{res.message}</Text>
        </Alert>
      )}
    </Card>
  );
}

function SatelliteGuide() {
  const { t } = useT();
  return (
    <Alert icon={<IconInfoCircle />} color="agri" variant="light" mb="md" title={t("Kako dobiti besplatan Copernicus pristup")}>
      <List type="ordered" size="sm" spacing={2}>
        <List.Item>{t("Prijavite se (ili registrujte besplatno) na")}{" "}
          <Anchor href="https://shapps.dataspace.copernicus.eu/dashboard" target="_blank" rel="noopener">shapps.dataspace.copernicus.eu/dashboard</Anchor></List.Item>
        <List.Item>{t("Otvorite User settings → OAuth clients → Create.")}</List.Item>
        <List.Item>{t("Kopirajte Client ID i Client Secret u polja ispod.")}</List.Item>
        <List.Item>{t("Uključite „Satelitske analize uključene“ i sačuvajte, pa kliknite „Provjeri vezu“.")}</List.Item>
      </List>
    </Alert>
  );
}

function SatelliteTools({ dirty }: { dirty: boolean }) {
  const { t } = useT();
  const st = useQuery({ queryKey: ["sentinel-status"], queryFn: () => http.get<SentinelStatus>("/api/v1/sentinel/status") });
  const reqs = useQuery({ queryKey: ["sentinel-requests"], queryFn: () => http.get<Record<string, unknown>[]>("/api/v1/sentinel/requests", { limit: 50 }) });
  const [res, setRes] = useState<{ ok: boolean; message: string } | null>(null);
  const test = useMutation({
    mutationFn: () => http.post<{ ok: boolean; message: string }>("/api/v1/settings/test/satelit"),
    onSuccess: setRes, onError: (e: Error) => setRes({ ok: false, message: e.message }),
  });
  const s = st.data;
  const pct = s && s.month_limit ? Math.min(100, (s.month_requests / s.month_limit) * 100) : 0;
  const pctPu = s && s.pu_limit ? Math.min(100, ((s.month_pu ?? 0) / s.pu_limit) * 100) : 0;
  return (
    <Stack mt="xl" gap="md">
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
        <Card withBorder radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t("Stanje")}</Text>
          <Group gap={6} mt={6}>
            <Badge color={s?.configured ? "green" : "gray"} variant="light">{s?.configured ? t("kredencijali uneseni") : t("nije podešeno")}</Badge>
            <Badge color={s?.enabled ? "green" : "gray"} variant="light">{s?.enabled ? t("uključeno") : t("isključeno")}</Badge>
          </Group>
          <Group mt="sm" gap="xs">
            <Button size="xs" leftSection={<IconWifi size={14} />} variant="light" loading={test.isPending} onClick={() => test.mutate()}>{t("Provjeri vezu")}</Button>
            {dirty && <Text size="xs" c="orange">{t("Prvo sačuvajte izmjene.")}</Text>}
          </Group>
          {res && (
            <Alert mt="sm" p="xs" color={res.ok ? "green" : "red"} icon={res.ok ? <IconCircleCheck size={16} /> : <IconCircleX size={16} />}>
              <Text size="xs">{res.message}</Text>
            </Alert>
          )}
        </Card>
        <Card withBorder radius="md">
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>{t("Potrošnja ovog mjeseca")}</Text>
          <SimpleGrid cols={2} spacing="md" mt={4}>
            <div>
              <Text fz={20} fw={750}>{s?.month_requests ?? "—"} <Text span size="xs" c="dimmed">/ {s?.month_limit || "∞"}</Text></Text>
              <Text size="xs" c="dimmed">{t("upita")}</Text>
              <Progress value={pct} mt={4} color={pct > 80 ? "orange" : "agri"} />
            </div>
            <div>
              <Text fz={20} fw={750}>{s?.month_pu != null ? fmtNum(s.month_pu, 1) : "—"} <Text span size="xs" c="dimmed">/ {s?.pu_limit || "∞"}</Text></Text>
              <Text size="xs" c="dimmed">{t("procesnih jedinica (PU)")}</Text>
              <Progress value={pctPu} mt={4} color={pctPu > 80 ? "orange" : "agri"} />
            </div>
          </SimpleGrid>
          <Text size="xs" c="dimmed" mt={6}>{t("Besplatan Copernicus nalog: 10.000 upita i 10.000 PU mjesečno, obnavlja se prvog u mjesecu.")}</Text>
          {s?.month_cache_hits != null && <Text size="xs" c="dimmed" mt={6}>{t("Iz keša (bez trošenja kvote)")}: {s.month_cache_hits}</Text>}
        </Card>
      </SimpleGrid>
      <div>
        <Text fw={650} mb="xs">{t("Istorija upita")}</Text>
        <DataTable rows={reqs.data ?? []} exportName="satelitski_upiti" maxHeight={360} dense columns={[
          { key: "created_at", label: "Vrijeme", render: (r) => fmtDateTime(r.created_at) },
          { key: "created_by", label: "Korisnik" },
          { key: "api", label: "API" },
          { key: "target_type", label: "Objekat", render: (r) => r.target_type ? `${r.target_type} #${r.target_id ?? ""}` : "—" },
          { key: "status", label: "Status", render: (r) => <Badge size="sm" variant="light"
            color={["ok", "done", "cache"].includes(String(r.status)) ? "green" : r.error ? "red" : "gray"}>{String(r.status ?? "—")}</Badge> },
          { key: "pu", label: "PU", type: "number" },
          { key: "duration_ms", label: "ms", type: "integer" },
          { key: "error", label: "Greška" },
        ]} />
      </div>
    </Stack>
  );
}

const INFO_LABELS: Record<string, string> = {
  app: "Aplikacija", version: "Verzija", env: "Okruženje", postgres: "PostgreSQL", postgis: "PostGIS", auth: "Prijava",
  "auth.issuer": "Keycloak realm (izdavalac)", "auth.web_client_id": "Web klijent", modules: "Uključeni moduli",
};

function flatten(o: Record<string, unknown>, prefix = ""): [string, unknown][] {
  return Object.entries(o).flatMap(([k, v]) => {
    const key = prefix ? `${prefix}.${k}` : k;
    return v && typeof v === "object" && !Array.isArray(v) ? flatten(v as Record<string, unknown>, key) : [[key, v] as [string, unknown]];
  });
}

function VersionsPanel() {
  const { t } = useT();
  const info = useQuery({ queryKey: ["sysinfo"], queryFn: () => http.get<Record<string, unknown>>("/api/v1/system/info") });
  return (
    <Paper withBorder radius="lg" p="lg">
      <Group gap="sm" mb="md"><ThemeIcon variant="light" size={36} radius="md"><IconServer size={17} /></ThemeIcon>
        <div><Title order={4}>{t("Verzije komponenti")}</Title><Text size="sm" c="dimmed">{t("Informacije o instaliranom sistemu (za podršku i održavanje).")}</Text></div></Group>
      {info.isLoading ? <Skeleton h={160} /> : (
        <Table verticalSpacing={8} withRowBorders>
          <Table.Tbody>
            {flatten(info.data ?? {}).map(([k, v]) => (
              <Table.Tr key={k}>
                <Table.Td w={260}><Text size="sm" c="dimmed">{t(INFO_LABELS[k] ?? k)}</Text></Table.Td>
                <Table.Td>
                  {Array.isArray(v) ? <Group gap={4}>{v.map((x) => <Badge key={String(x)} variant="light">{String(x)}</Badge>)}</Group>
                    : k === "env" ? <Badge color={v === "prod" ? "green" : "yellow"} variant="light">{String(v)}</Badge>
                    : <Text size="sm" fw={500} style={{ wordBreak: "break-word" }}>{String(v ?? "—")}</Text>}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
    </Paper>
  );
}

/** Stanje mreže trenutnog zahtjeva: da li je administrator sada na VPN-u / u internoj mreži. */
function NetworkStatus() {
  const { t } = useT();
  const q = useQuery({ queryKey: ["network-status"], refetchInterval: 15_000,
    queryFn: () => http.get<{ internal: boolean | null; ip?: string; guard_on: boolean; disabled_by_env: boolean; my_internal_roles: string[] }>("/api/v1/settings/network-status") });
  const d = q.data;
  if (!d) return null;
  const where = d.internal === true ? t("interna mreža / VPN") : d.internal === false ? t("internet (van VPN-a)") : t("nepoznato (direktno na API)");
  return (
    <Alert mt="sm" color={d.internal === false ? "orange" : "green"} variant="light" title={`${t("Vaš pristup")}: ${where}`}>
      <Text size="sm">{t("Adresa")}: <b data-no-translit="true">{d.ip ?? "—"}</b> · {t("zaštita")}: <b>{d.disabled_by_env ? t("isključena u .env (NETWORK_GUARD=off)") : d.guard_on ? t("uključena") : t("isključena")}</b></Text>
      {d.internal === false && <Text size="sm" mt={4}>{t("Zaštitu možete uključiti tek kad se povežete na VPN — inače biste sebi zaključali pristup.")}</Text>}
      <Text size="xs" c="dimmed" mt={4}>{t("Interne mreže (VPN, LAN Agencije) određuje informatička služba u .env: INTERNAL_NETS. Uputstvo: docs/VPN_I_ZASTITA.md.")}</Text>
    </Alert>
  );
}
